import { Location } from '@angular/common';
// Type-only — used just as a parameter type below, so importing this util (e.g. from
// low-level components/services) stays light and doesn't pull TelemetryService into the graph.
import type { TelemetryService } from 'src/app/services/telemetry.service';
import { LangCode } from 'src/app/models/localization.model';

// values should be all lower case for consistency
export enum ActionsQueryParamKeys {
  AUTO_START_CALL = 'startcall',
  DEFAULT_GALLERY_VIEW = 'gv',
  SKIP_DEVICE_SETUP = 'skipdevicesetup',
  /** Embed shell (?standalone=true): hides leave-space (shows contact-support instead), invite, and copy-link in top controls; also hides attendance "Create Event". Can also be enabled via institution hideSpaceBackLinks unless ?standalone=false. */
  STAND_ALONE_MODE = 'standalone',
  OBSERVER_MODE = 'observer',
  LANGUAGE = 'language',
  BROADCAST_EVENTS = 'broadcastevents',
  POST_INSTITUTION_UPGRADE = 'institutionupgraded',
  AUTO_LOCAL_RECORDING = 'startlocalrecording',
  SKIP_CALL_HOST_CHECK = 'skipcallhostcheck',
  // Embedded AI-tutor params. Registered here so they're recognized + PRESERVED through
  // the app's action-param plumbing — notably auth.service's redirect (which only carries
  // extractActionsQueryParams()), so they survive /token-login and reach the space route.
  AGENT_ID = 'agentid',
  AI_TUTOR = 'aitutor',
  // Luna workspace mode: tabs for the whiteboard and each app, full-screen and split apps,
  // and a whiteboard that scrolls vertically. Only applies on top of AI-tutor mode.
  LUNA_WORKSPACE = 'lunaworkspace',
  // Practice Roleplay — set by the "Start" button so the space dispatches to the
  // `practice-roleplay-<env>` worker (a student persona + silent coaching) instead of the
  // real tutor. Registered here so it survives the action-param redirect plumbing.
  ROLEPLAY = 'roleplay',
  // Partner "persona" embed — an integration partner iframes a space with `?persona=true`.
  // Behaves like roleplay (roleplay worker + roleplay UI, persona driven by the `?agentId=`
  // config, not a Pencil personaId) EXCEPT the end-of-session report is forwarded to the
  // embedding parent host (postMessage) instead of showing the in-app review dialog.
  PERSONA = 'persona',
  // AI-tutor model-selection overrides, passed to the LiveKit worker via dispatch metadata.
  // stt: aai|deepgram · llm: small|medium|max|kimi · thinking: true|false · tts: cartesia|elevenlabs
  // · td (turn detector): v1 (default, text) | v2 (audio) | stt.
  STT = 'stt',
  LLM = 'llm',
  THINKING = 'thinking',
  TTS = 'tts',
  TD = 'td',
  // AI-tutor silence-nudge timing (feature F3), seconds. Forwarded to the worker via
  // dispatch metadata; each overrides its env default (SILENCE_GRACE_S / SILENCE_COUNTDOWN_S).
  SILENCE_GRACE = 'silencegrace',
  SILENCE_COUNTDOWN = 'silencecountdown',
  /** Space layout/behavior via ?mode= (see SpaceViewMode). Independent of standalone embed chrome. */
  MODE = 'mode',
}

/** Space view modes passed via ?mode= (values are lower case). */
export enum SpaceViewMode {
  /** Hide Pencil call UI and call prompts; users rely on the host meeting for audio/video. */
  WHITEBOARD_ONLY = 'whiteboardonly',
}

export enum ActionsQueryParamValues {
  TRUE = 'true',
  FALSE = 'false',
}

/**
 * Query params for third-party meeting embed entry (Teams, Zoom).
 * Both keys are set together for meeting tabs; they overlap in intent (stay in the host meeting)
 * but control different layers:
 * - standalone: embed shell — no leave/invite/copy-link chrome
 * - mode=whiteboardonly: hide Pencil call stack — A/V stays in Teams/Zoom
 */
export const MEETING_EMBED_SPACE_QUERY_PARAMS: { [K in ActionsQueryParamKeys]?: string } = {
  [ActionsQueryParamKeys.STAND_ALONE_MODE]: ActionsQueryParamValues.TRUE,
  [ActionsQueryParamKeys.MODE]: SpaceViewMode.WHITEBOARD_ONLY,
};

export function extractActionsQueryParams(): Record<string, string> {
  const params: Record<string, string> = {};
  new URLSearchParams(location.search).forEach((value, key) => {
    const sanitizedKey = sanitizeActionsQueryParam(key);
    if (isActionsQueryParamKey(sanitizedKey)) {
      params[sanitizedKey] = sanitizeActionsQueryParam(value);
    }
  });
  return params;
}

export function sanitizeActionsQueryParam(param: string): string {
  return param.toLowerCase().trim();
}

export function isActionsQueryParamKey(sanitizedParam: string): boolean {
  return (Object.values(ActionsQueryParamKeys) as string[]).includes(sanitizedParam);
}

// doesn't include the question mark.
export function getActionsSearchQuery(): string {
  return new URLSearchParams(extractActionsQueryParams()).toString();
}

export function removeQueryParamsFromUrl(
  locationService: Location,
  telemetryService: TelemetryService,
  queryParamsToBeDeleted: ActionsQueryParamKeys[],
): void {
  const currentQueryParams = extractActionsQueryParams();

  const hasParamsToDelete: boolean = !!queryParamsToBeDeleted.find(
    (param) => param in currentQueryParams,
  );
  if (!hasParamsToDelete) {
    return;
  }

  const newQueryParams = new URLSearchParams(currentQueryParams);
  queryParamsToBeDeleted.forEach((param) => {
    newQueryParams.delete(param);
  });

  const currentState = locationService.getState();
  try {
    locationService.replaceState(location.pathname, newQueryParams.toString(), currentState);
  } catch (error) {
    telemetryService.errorEvent('Failed_to_replace_state_after_changing_language', error);
  }
}

export function checkIfQueryParamSetToTrue(queryParam: ActionsQueryParamKeys): boolean {
  const queryParams = extractActionsQueryParams();

  return queryParams[queryParam] === ActionsQueryParamValues.TRUE;
}

/**
 * True for a partner "persona" embed (`?persona=true`). Persona sessions run the roleplay
 * worker + roleplay UI (see {@link isRoleplayLikeSession}) but forward the end-of-session
 * report to the embedding parent host instead of showing the in-app review dialog.
 */
export function isPersonaSession(): boolean {
  return checkIfQueryParamSetToTrue(ActionsQueryParamKeys.PERSONA);
}

/**
 * True for any session that should use the Practice Roleplay worker + roleplay UI (persona
 * tile + orb, no side chat panel): a real Practice Roleplay session (`?roleplay=true`) OR a
 * partner persona embed (`?persona=true`). Use this for layout/dispatch decisions; use
 * {@link isPersonaSession} for the few places persona diverges from roleplay.
 */
export function isRoleplayLikeSession(): boolean {
  return (
    checkIfQueryParamSetToTrue(ActionsQueryParamKeys.ROLEPLAY) ||
    checkIfQueryParamSetToTrue(ActionsQueryParamKeys.PERSONA)
  );
}

export function checkIfQueryParamSetToFalse(queryParam: ActionsQueryParamKeys): boolean {
  const queryParams = extractActionsQueryParams();

  return queryParams[queryParam] === ActionsQueryParamValues.FALSE;
}

/** The embedded-tutor `agentId` from the URL, or '' if absent. Read via the recognized
 * action params so it rides the app's redirect plumbing (see AGENT_ID). Agent-config ids
 * are lowercase-hex Mongo ObjectIds, so the value sanitization is a no-op. */
export function getAgentIdQueryParam(): string {
  return extractActionsQueryParams()[ActionsQueryParamKeys.AGENT_ID] ?? '';
}

/** AI-tutor model-selection overrides from the URL, as a `{param: value}` map ready to
 * merge into the dispatch metadata. Only present params are included; absent → the worker
 * keeps its current defaults. Values are sanitized (lowercased/trimmed). Read via the
 * recognized action params so they ride the redirect plumbing (see the enum comment).
 * e.g. `?llm=medium&stt=deepgram&thinking=false&tts=elevenlabs`. */
export function getModelSelectionParams(): Record<string, string> {
  const params = extractActionsQueryParams();
  const out: Record<string, string> = {};
  for (const key of [
    ActionsQueryParamKeys.STT,
    ActionsQueryParamKeys.LLM,
    ActionsQueryParamKeys.THINKING,
    ActionsQueryParamKeys.TTS,
    ActionsQueryParamKeys.TD,
    ActionsQueryParamKeys.SILENCE_GRACE,
    ActionsQueryParamKeys.SILENCE_COUNTDOWN,
  ]) {
    const value = params[key];
    if (value) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * The query param overrides the institution setting: either the param is set to true, or the
 * institution's hideSpaceBackLinks is on and the param is not set to false.
 * Lives here rather than only in ActionsQueryParamsService so that service's own dependencies can
 * resolve standalone mode without a circular dependency.
 */
export function checkIfStandAloneMode(hideSpaceBackLinks: boolean | undefined): boolean {
  return (
    checkIfQueryParamSetToTrue(ActionsQueryParamKeys.STAND_ALONE_MODE) ||
    (Boolean(hideSpaceBackLinks) &&
      !checkIfQueryParamSetToFalse(ActionsQueryParamKeys.STAND_ALONE_MODE))
  );
}

export function checkIfSpaceViewMode(mode: SpaceViewMode): boolean {
  const queryParams = extractActionsQueryParams();

  return queryParams[ActionsQueryParamKeys.MODE] === mode;
}

// this was added here to break a circular dependency if the user service relied on the actions query params service
// if the lang query param is not set or not valid it will return an empty string
// otherwise it will return the selected -original not sanitized- language code
export function getLanguageQueryParam(enabledLangs: LangCode[]): string {
  const queryParams = extractActionsQueryParams();
  const sanitizedLanguageCode = queryParams[ActionsQueryParamKeys.LANGUAGE];

  if (!sanitizedLanguageCode) {
    return '';
  }

  const selectedLang = enabledLangs.find(
    (lang) => sanitizeActionsQueryParam(lang.code) === sanitizedLanguageCode,
  );
  return selectedLang?.code ?? '';
}
